import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Receipt, X, Printer, ArrowUUpLeft, Prohibit, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import TicketPrint from "../components/TicketPrint";

const fmt = (n) => new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2 }).format(n || 0);
const fmtDate = (d) => new Date(d).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });

const STATUS = {
  completed: { label: "Completada", cls: "bg-white/5 text-zinc-400" },
  cancelled: { label: "Cancelada", cls: "bg-red-500/10 text-red-400" },
  refunded: { label: "Devuelta", cls: "bg-amber-500/10 text-amber-400" },
  partial_return: { label: "Dev. Parcial", cls: "bg-amber-500/10 text-amber-400" },
};

export default function Sales() {
  const [sales, setSales] = useState([]);
  const [selected, setSelected] = useState(null);
  const [printSale, setPrintSale] = useState(null);
  const [query, setQuery] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [creditNote, setCreditNote] = useState(null);
  const { user } = useAuth();

  const load = () => api.get("/sales").then(({ data }) => setSales(data));

  useEffect(() => { load(); }, []);

  const filtered = sales.filter((s) => {
    const q = query.toLowerCase();
    return !q || s.customer_name.toLowerCase().includes(q) || s.sale_id.toLowerCase().includes(q) || s.seller_name.toLowerCase().includes(q);
  });

  const closeAllModals = () => {
    setSelected(null);
    setCancelOpen(false);
    setReturnOpen(false);
  };

  const onActionSuccess = async (creditNoteData) => {
    const saleSnapshot = selected;
    closeAllModals();
    await load();
    // Open credit note ticket for printing
    if (creditNoteData && saleSnapshot) {
      setCreditNote({ sale: saleSnapshot, note: creditNoteData });
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="sales-page">
      <header className="mb-6">
        <p className="text-xs uppercase tracking-[0.3em] text-turquoise font-mono mb-1">Trazabilidad</p>
        <h1 className="text-3xl md:text-4xl font-heading font-bold">Historial de Ventas</h1>
        <p className="text-zinc-400 mt-1 text-sm">{sales.length} operaciones registradas</p>
      </header>

      <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por cliente, vendedor o ID..."
        className="w-full max-w-md bg-[#0F1115] border border-white/10 rounded-md px-4 py-2 text-sm mb-4 font-mono" />

      <div className="glass-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/5">
              <tr>
                <th className="text-left p-4 font-mono">ID</th>
                <th className="text-left p-4 font-mono">Fecha</th>
                <th className="text-left p-4 font-mono">Cliente</th>
                <th className="text-left p-4 font-mono">Vendedor</th>
                <th className="text-left p-4 font-mono">Estado</th>
                <th className="text-left p-4 font-mono">Método</th>
                <th className="text-right p-4 font-mono">Items</th>
                <th className="text-right p-4 font-mono">Total</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="9" className="p-12 text-center text-zinc-500">Sin operaciones</td></tr>
              ) : filtered.map((s) => {
                const st = STATUS[s.status] || STATUS.completed;
                const dim = s.status === "cancelled" || s.status === "refunded";
                return (
                  <tr key={s.sale_id} className={`border-b border-white/5 hover:bg-white/[0.02] ${dim ? "opacity-50" : ""}`}>
                    <td className="p-4 font-mono text-xs text-zinc-500">{s.sale_id.slice(-8)}</td>
                    <td className="p-4 text-xs font-mono">{fmtDate(s.created_at)}</td>
                    <td className="p-4 font-medium">{s.customer_name}</td>
                    <td className="p-4 text-zinc-400 text-xs">{s.seller_name}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-[10px] uppercase font-mono tracking-wider ${st.cls}`}>{st.label}</span>
                    </td>
                    <td className="p-4 text-xs">
                      <span className={`px-2 py-1 rounded-full text-[10px] uppercase font-mono tracking-wider ${s.payment_method === "billetera" ? "bg-turquoise/10 text-turquoise" : s.payment_method === "mixto" ? "bg-cyan-500/10 text-cyan-400" : "bg-white/5 text-zinc-400"}`}>
                        {s.payment_method}
                      </span>
                    </td>
                    <td className="p-4 text-right font-mono">{s.items.length}</td>
                    <td className={`p-4 text-right font-mono ${dim ? "line-through" : "text-turquoise"}`}>$ {fmt(s.total)}</td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setPrintSale(s)} className="text-zinc-400 hover:text-turquoise" data-testid={`sale-print-${s.sale_id}`} title="Imprimir ticket">
                          <Printer size={16} />
                        </button>
                        <button onClick={() => setSelected(s)} className="text-zinc-400 hover:text-turquoise" data-testid={`sale-detail-${s.sale_id}`}>
                          <Receipt size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selected && !cancelOpen && !returnOpen && (
        <SaleDetailModal
          sale={selected}
          user={user}
          onClose={() => setSelected(null)}
          onPrint={() => { setPrintSale(selected); setSelected(null); }}
          onCancel={() => setCancelOpen(true)}
          onReturn={() => setReturnOpen(true)}
        />
      )}

      {cancelOpen && selected && (
        <CancelSaleModal sale={selected} onClose={() => setCancelOpen(false)} onSuccess={onActionSuccess} />
      )}

      {returnOpen && selected && (
        <ReturnSaleModal sale={selected} onClose={() => setReturnOpen(false)} onSuccess={onActionSuccess} />
      )}

      {printSale && <TicketPrint sale={printSale} onClose={() => setPrintSale(null)} />}

      {creditNote && (
        <TicketPrint
          sale={creditNote.sale}
          mode="credit_note"
          creditNote={creditNote.note}
          onClose={() => setCreditNote(null)}
        />
      )}
    </div>
  );
}

function SaleDetailModal({ sale, user, onClose, onPrint, onCancel, onReturn }) {
  const isActive = sale.status === "completed" || sale.status === "partial_return";
  const canCancel = user?.role === "admin" && sale.status === "completed";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="glass-card rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
            <Receipt size={20} className="text-turquoise" /> Detalle de venta
          </h3>
          <button onClick={onClose} data-testid="sale-detail-close"><X size={20} /></button>
        </div>
        {(sale.status === "cancelled" || sale.status === "refunded" || sale.status === "partial_return") && (
          <div className={`mb-4 px-3 py-2 rounded-md text-xs font-mono ${sale.status === "cancelled" ? "bg-red-500/10 text-red-400 border border-red-500/20" : "bg-amber-500/10 text-amber-400 border border-amber-500/20"}`}>
            {sale.status === "cancelled"
              ? `Venta CANCELADA — ${sale.cancel_reason || "sin motivo"} (por ${sale.cancelled_by || "—"})`
              : `Devuelta total/parcial — Reembolso $ ${fmt(sale.refunded_amount || 0)}`}
          </div>
        )}
        <div className="text-xs font-mono space-y-1 mb-4">
          <div className="flex justify-between"><span className="text-zinc-500">ID:</span><span>{sale.sale_id}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Fecha:</span><span>{fmtDate(sale.created_at)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Cliente:</span><span className="text-white">{sale.customer_name}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Vendedor:</span><span>{sale.seller_name}</span></div>
        </div>
        <div className="border-y border-white/5 py-3 space-y-2">
          {sale.items.map((it) => (
            <div key={`${sale.sale_id}-${it.product_id}`} className="flex justify-between text-sm">
              <div>
                <p>{it.name}</p>
                <p className="text-[10px] text-zinc-500 font-mono">{it.qty} × $ {fmt(it.price)}</p>
              </div>
              <p className="font-mono">$ {fmt(it.subtotal)}</p>
            </div>
          ))}
        </div>
        <div className="pt-3 space-y-1 text-sm font-mono">
          <div className="flex justify-between"><span className="text-zinc-500">Total</span><span>$ {fmt(sale.total)}</span></div>
          {sale.wallet_used > 0 && <div className="flex justify-between text-turquoise"><span>Billetera</span><span>$ {fmt(sale.wallet_used)}</span></div>}
          <div className="flex justify-between"><span className="text-zinc-500">Recibido</span><span>$ {fmt(sale.paid_amount)}</span></div>
          <div className="flex justify-between border-t border-white/5 pt-2 mt-2 text-turquoise text-base"><span>Vuelto</span><span>$ {fmt(sale.change_amount)}</span></div>
        </div>

        <div className="flex gap-2 mt-5 flex-wrap">
          <button onClick={onPrint} data-testid="sale-detail-print-btn"
            className="flex-1 min-w-[140px] py-2.5 rounded-md text-sm font-semibold text-black bg-brand-gradient hover:opacity-90 flex items-center justify-center gap-2">
            <Printer size={16} weight="bold" /> Imprimir
          </button>
          {isActive && (
            <button onClick={onReturn} data-testid="sale-return-btn"
              className="flex-1 min-w-[140px] py-2.5 rounded-md text-sm font-semibold border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 transition flex items-center justify-center gap-2">
              <ArrowUUpLeft size={16} weight="bold" /> Devolver items
            </button>
          )}
          {canCancel && (
            <button onClick={onCancel} data-testid="sale-cancel-btn"
              className="flex-1 min-w-[140px] py-2.5 rounded-md text-sm font-semibold border border-red-500/50 text-red-400 hover:bg-red-500/10 transition flex items-center justify-center gap-2">
              <Prohibit size={16} weight="bold" /> Cancelar venta
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CancelSaleModal({ sale, onClose, onSuccess }) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/sales/${sale.sale_id}/cancel`, { reason });
      toast.success(
        data.refunded_to_wallet
          ? `Venta cancelada. $ ${fmt(sale.total)} acreditados en billetera del cliente.`
          : "Venta cancelada. Stock restaurado."
      );
      onSuccess({
        kind: "cancellation",
        items: sale.items.map((it) => ({ name: it.name, qty: it.qty, unit_price: it.price, refund: it.subtotal })),
        refund_total: sale.total,
        reason: reason || "Cancelación de venta",
        refunded_to_wallet: data.refunded_to_wallet,
        new_wallet_balance: data.new_wallet_balance,
        return_id: `cancel-${sale.sale_id.slice(-8)}`,
        created_at: new Date().toISOString(),
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cancelar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="cancel-sale-modal">
      <div className="glass-card rounded-xl w-full max-w-md p-6 border border-red-500/20">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md flex items-center justify-center bg-red-500/10">
            <Warning size={20} weight="duotone" className="text-red-400" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-red-400 font-mono">Acción irreversible</p>
            <h3 className="text-lg font-heading font-bold">Cancelar venta</h3>
          </div>
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          Se va a restaurar el stock de <strong className="text-white">{sale.items.length} items</strong>
          {sale.customer_id ? <> y acreditar <strong className="text-turquoise">$ {fmt(sale.total)}</strong> a la billetera de <strong className="text-white">{sale.customer_name}</strong>.</> : <> (cliente no registrado, no se acredita billetera).</>}
        </p>
        <label className="block text-xs uppercase tracking-wider text-zinc-500 font-mono mb-2">Motivo (opcional)</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Ej: error de tipeo, cliente arrepentido, etc."
          data-testid="cancel-reason-input"
          className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/10 focus:border-red-400 outline-none text-white text-sm resize-none"
        />
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} data-testid="cancel-modal-back" className="flex-1 py-2.5 rounded-md border border-white/10 text-sm font-medium hover:bg-white/5">
            Volver
          </button>
          <button onClick={submit} disabled={loading} data-testid="cancel-modal-confirm"
            className="flex-1 py-2.5 rounded-md text-sm font-semibold text-white bg-red-500 hover:bg-red-400 disabled:opacity-40 transition">
            {loading ? "Cancelando..." : "Confirmar cancelación"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReturnSaleModal({ sale, onClose, onSuccess }) {
  const returnedMap = Object.fromEntries((sale.returned_items || []).map((r) => [r.product_id, r.qty]));
  const [qtyMap, setQtyMap] = useState(() =>
    Object.fromEntries(sale.items.map((it) => [it.product_id, 0]))
  );
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const setQty = (pid, v, max) => {
    const n = Math.max(0, Math.min(max, parseInt(v) || 0));
    setQtyMap((m) => ({ ...m, [pid]: n }));
  };

  const refundTotal = sale.items.reduce((sum, it) => sum + (qtyMap[it.product_id] || 0) * it.price, 0);
  const totalQty = Object.values(qtyMap).reduce((a, b) => a + b, 0);

  const submit = async () => {
    if (totalQty === 0) return toast.error("Seleccioná al menos un item para devolver");
    if (!reason.trim()) return toast.error("Indicá el motivo de la devolución");
    setLoading(true);
    try {
      const items = Object.entries(qtyMap)
        .filter(([, q]) => q > 0)
        .map(([product_id, qty]) => ({ product_id, qty }));
      const { data } = await api.post(`/sales/${sale.sale_id}/return`, { items, reason: reason.trim() });
      toast.success(
        sale.customer_id
          ? `Devolución registrada. $ ${fmt(data.refund_total)} acreditados al cliente.`
          : `Devolución registrada. Stock restaurado.`
      );
      const itemsForNote = sale.items
        .filter((it) => (qtyMap[it.product_id] || 0) > 0)
        .map((it) => ({
          name: it.name,
          qty: qtyMap[it.product_id],
          unit_price: it.price,
          refund: qtyMap[it.product_id] * it.price,
        }));
      onSuccess({
        kind: "return",
        items: itemsForNote,
        refund_total: data.refund_total,
        reason: reason.trim(),
        refunded_to_wallet: !!data.new_wallet_balance || (sale.customer_id && data.refund_total > 0),
        new_wallet_balance: data.new_wallet_balance,
        return_id: data.return_id,
        created_at: new Date().toISOString(),
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al procesar devolución");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="return-sale-modal">
      <div className="glass-card rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto border border-amber-500/20">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md flex items-center justify-center bg-amber-500/10">
            <ArrowUUpLeft size={20} weight="duotone" className="text-amber-400" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-400 font-mono">Devolución parcial</p>
            <h3 className="text-lg font-heading font-bold">Devolver items</h3>
          </div>
        </div>

        <p className="text-xs text-zinc-400 mb-3">Seleccioná la cantidad a devolver de cada item:</p>
        <div className="space-y-2 mb-4 border-y border-white/5 py-3">
          {sale.items.map((it) => {
            const already = returnedMap[it.product_id] || 0;
            const max = it.qty - already;
            const current = qtyMap[it.product_id] || 0;
            return (
              <div key={it.product_id} className="flex items-center gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <p className="truncate">{it.name}</p>
                  <p className="text-[10px] text-zinc-500 font-mono">
                    Vendido: {it.qty} {already > 0 && <span className="text-amber-400">— Ya devuelto: {already}</span>} — Disponible: {max}
                  </p>
                </div>
                <input
                  type="number"
                  min="0"
                  max={max}
                  value={current}
                  onChange={(e) => setQty(it.product_id, e.target.value, max)}
                  disabled={max === 0}
                  data-testid={`return-qty-${it.product_id}`}
                  className="w-16 px-2 py-1.5 rounded bg-white/[0.03] border border-white/10 focus:border-amber-400 outline-none text-right font-mono text-sm disabled:opacity-30"
                />
                <p className="w-20 text-right font-mono text-xs text-turquoise">$ {fmt(current * it.price)}</p>
              </div>
            );
          })}
        </div>

        <div className="flex justify-between items-center mb-4 px-3 py-2 bg-amber-500/5 rounded-md border border-amber-500/20">
          <span className="text-xs uppercase tracking-wider text-amber-400 font-mono">Total a devolver</span>
          <span className="text-lg font-mono font-medium text-amber-400">$ {fmt(refundTotal)}</span>
        </div>

        <label className="block text-xs uppercase tracking-wider text-zinc-500 font-mono mb-2">Motivo <span className="text-red-400">*</span></label>
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="return-reason-select"
          className="w-full px-3 py-2 mb-3 rounded-md bg-white/[0.03] border border-white/10 focus:border-amber-400 outline-none text-white text-sm"
        >
          <option value="">— Elegir motivo —</option>
          <option value="Defectuoso">Defectuoso</option>
          <option value="No funciona">No funciona</option>
          <option value="Producto equivocado">Producto equivocado</option>
          <option value="Cambio">Cambio por otro producto</option>
          <option value="Arrepentimiento">Arrepentimiento del cliente</option>
          <option value="Otro">Otro</option>
        </select>
        {reason === "Otro" && (
          <input
            type="text"
            placeholder="Especificar..."
            onChange={(e) => setReason("Otro: " + e.target.value)}
            data-testid="return-reason-other"
            className="w-full px-3 py-2 mb-3 rounded-md bg-white/[0.03] border border-white/10 focus:border-amber-400 outline-none text-white text-sm"
          />
        )}

        {!sale.customer_id && (
          <p className="text-[11px] text-zinc-500 font-mono mb-3">Venta sin cliente — solo se restaurará stock, no se acreditará billetera.</p>
        )}

        <div className="flex gap-3">
          <button onClick={onClose} data-testid="return-modal-cancel" className="flex-1 py-2.5 rounded-md border border-white/10 text-sm font-medium hover:bg-white/5">
            Cancelar
          </button>
          <button onClick={submit} disabled={loading || totalQty === 0 || !reason} data-testid="return-modal-confirm"
            className="flex-1 py-2.5 rounded-md text-sm font-semibold text-black bg-amber-400 hover:bg-amber-300 disabled:opacity-40 disabled:cursor-not-allowed transition">
            {loading ? "Procesando..." : `Devolver ${totalQty} items`}
          </button>
        </div>
      </div>
    </div>
  );
}
